import {
  CartLinesUpdateEvent,
  CartErrorEvent,
  StandardEvents,
} from '@shopify/events';

class CompanionProductSelector extends HTMLElement {
  #abortController = new AbortController();
  #isSubmitting = false;
  #productScope = null;

  connectedCallback() {
    const { signal } = this.#abortController;

    this.#productScope =
      this.closest('.product-details') ||
      this.closest('.shopify-section') ||
      document;

    this.addEventListener(
      'change',
      this.#handleChange,
      { signal }
    );

    this.#productScope.addEventListener(
      'product-form:before-add',
      this.#handleBeforeAdd,
      { signal }
    );

    this.#productScope.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    this.#syncUI();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /* =========================================================
     CURRENT STATE
  ========================================================= */

  get #choice() {
    return (
      this.querySelector(
        'input[name^="companion-choice-"]:checked'
      )?.value || 'insert_only'
    );
  }

  get #selectedCompanionVariantInput() {
    return this.querySelector(
      'input[name^="companion-variant-"]:checked'
    );
  }

  get #withCompanionChoiceInput() {
    return this.querySelector(
      'input[name^="companion-choice-"][value="with_companion"]'
    );
  }

  /* =========================================================
     CHANGE HANDLING
  ========================================================= */

  #handleChange = (event) => {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    if (
      target.matches(
        'input[name^="companion-choice-"]'
      )
    ) {
      this.#syncUI();
      return;
    }

    if (
      target.matches(
        'input[name^="companion-variant-"]'
      )
    ) {
      /*
       * Clicking a wall plate variant automatically
       * enables "No, add a wall plate".
       */
      const companionChoice =
        this.#withCompanionChoiceInput;

      if (
        companionChoice instanceof HTMLInputElement &&
        !companionChoice.checked
      ) {
        companionChoice.checked = true;
      }

      this.#syncUI();
    }
  };

  /* =========================================================
     UI
  ========================================================= */

  #syncUI() {
    this.#syncChoiceCards();
    this.#syncVariantSwatches();
    this.#syncSelectedVariantTitle();
    this.#syncVariantAvailability();
  }

  #syncChoiceCards() {
    const selectedChoice =
      this.#choice;

    this.querySelectorAll(
      '[data-choice-card]'
    ).forEach((card) => {
      card.classList.toggle(
        'is-selected',
        card.dataset.choiceCard ===
          selectedChoice
      );
    });
  }

  #syncVariantSwatches() {
    const selectedInput =
      this.#selectedCompanionVariantInput;

    this.querySelectorAll(
      '.companion-product-selector__swatch'
    ).forEach((swatch) => {
      const input =
        swatch.querySelector(
          'input[name^="companion-variant-"]'
        );

      swatch.classList.toggle(
        'is-selected',
        input === selectedInput
      );
    });
  }

  #syncSelectedVariantTitle() {
    const selectedInput =
      this.#selectedCompanionVariantInput;

    const titleElement =
      this.querySelector(
        '[data-selected-variant-title]'
      );

    if (
      !selectedInput ||
      !titleElement
    ) {
      return;
    }

    titleElement.textContent =
      selectedInput.dataset.variantTitle || '';
  }

  #syncVariantAvailability() {
    const variantsWrapper =
      this.querySelector(
        '[data-companion-options]'
      );

    if (!(variantsWrapper instanceof HTMLElement)) {
      return;
    }

    const isActive =
      this.#choice === 'with_companion';

    variantsWrapper.classList.toggle(
      'is-active',
      isActive
    );

    variantsWrapper.setAttribute(
      'aria-disabled',
      isActive ? 'false' : 'true'
    );
  }

  /* =========================================================
     BEFORE HORIZON ADDS TO CART
  ========================================================= */

  #handleBeforeAdd = async (event) => {
    /*
     * Insert only:
     * do nothing and allow Horizon to continue normally.
     */
    if (this.#choice !== 'with_companion') {
      return;
    }

    /*
     * Stop accidental double click / duplicate request.
     */
    if (this.#isSubmitting) {
      event.preventDefault();
      return;
    }

    const productForm =
      event.detail?.productForm;

    if (!(productForm instanceof HTMLElement)) {
      return;
    }

    /*
     * Ensure this is the product form belonging
     * to the same product section.
     */
    const eventScope =
      productForm.closest('.product-details') ||
      productForm.closest('.shopify-section');

    const companionScope =
      this.closest('.product-details') ||
      this.closest('.shopify-section');

    if (
      eventScope &&
      companionScope &&
      eventScope !== companionScope
    ) {
      return;
    }

    /*
     * Stop Horizon's original Insert-only request.
     * Our request adds Insert + Wall Plate together.
     */
    event.preventDefault();

    const parentVariantId =
      this.#getMainVariantId(
        productForm
      );

    if (!parentVariantId) {
      this.#showError(
        'Unable to determine the selected insert variant.'
      );

      return;
    }

    const companionInput =
      this.#selectedCompanionVariantInput;

    const companionVariantId =
      Number(
        companionInput?.value || 0
      );

    if (!companionVariantId) {
      this.#showError(
        'Please select a wall plate.'
      );

      return;
    }

    const quantity =
      this.#getQuantity(
        productForm
      );

    await this.#addSetToCart({
      productForm,
      parentVariantId,
      companionVariantId,
      quantity,
    });
  };

  /* =========================================================
     MAIN PRODUCT VARIANT
  ========================================================= */

  #getMainVariantId(productForm) {
    const refVariantId =
      productForm.refs?.variantId?.value;

    if (refVariantId) {
      return Number(
        refVariantId
      );
    }

    const variantInput =
      productForm.querySelector(
        'input[name="id"]'
      );

    if (
      variantInput instanceof HTMLInputElement &&
      variantInput.value
    ) {
      return Number(
        variantInput.value
      );
    }

    return Number(
      this.dataset.mainVariantId || 0
    );
  }

  /* =========================================================
     DEFAULT HORIZON QUANTITY
  ========================================================= */

  #getQuantity(productForm) {
    const quantitySelector =
      productForm.refs?.quantitySelector;

    if (
      quantitySelector &&
      typeof quantitySelector.getValue === 'function'
    ) {
      const quantity =
        Number(
          quantitySelector.getValue()
        );

      if (
        Number.isFinite(quantity) &&
        quantity > 0
      ) {
        return quantity;
      }
    }

    const quantityInput =
      productForm.querySelector(
        'input[name="quantity"]'
      );

    if (
      quantityInput instanceof HTMLInputElement
    ) {
      const quantity =
        Number(
          quantityInput.value
        );

      if (
        Number.isFinite(quantity) &&
        quantity > 0
      ) {
        return quantity;
      }
    }

    const defaultQuantity =
      Number(
        productForm.dataset.quantityDefault || 1
      );

    if (
      Number.isFinite(defaultQuantity) &&
      defaultQuantity > 0
    ) {
      return defaultQuantity;
    }

    return 1;
  }

  /* =========================================================
     ADD SET
  ========================================================= */

  async #addSetToCart({
    productForm,
    parentVariantId,
    companionVariantId,
    quantity,
  }) {
    if (this.#isSubmitting) {
      return;
    }

    this.#isSubmitting = true;

    this.#clearStatus();

    const addToCartContainers =
      productForm.querySelectorAll(
        'add-to-cart-component'
      );

    addToCartContainers.forEach(
      (container) => {
        if (
          typeof container.disable === 'function'
        ) {
          container.disable();
        }
      }
    );

    /*
     * IMPORTANT:
     *
     * Stable set key.
     *
     * Same:
     * Insert variant
     * +
     * Wall Plate variant
     *
     * always produces the same cart-line properties.
     *
     * Therefore Shopify can merge quantities.
     *
     * Different Wall Plate variant creates
     * a different set.
     */
    const setKey =
      `${parentVariantId}-${companionVariantId}`;

    const items = [
      /*
       * Parent = current Insert
       */
      {
        id:
          parentVariantId,

        quantity,

        properties: {
          _switchups_set:
            'true',

          _switchups_set_key:
            setKey,

          _switchups_companion_variant:
            String(
              companionVariantId
            ),
        },
      },

      /*
       * Child = selected Wall Plate
       */
      {
        id:
          companionVariantId,

        quantity,

        parent_id:
          parentVariantId,

        properties: {
          _switchups_set:
            'true',

          _switchups_set_key:
            setKey,

          _switchups_companion:
            'true',

          _switchups_parent_variant:
            String(
              parentVariantId
            ),
        },
      },
    ];

    const sectionIds =
      this.#getCartSectionIds();

    const deferredEventPromise =
      CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action:
          'add',

        context:
          'product',

        lines:
          items.map((item) => ({
            merchandiseId:
              String(item.id),

            quantity:
              item.quantity,
          })),

        promise:
          deferredEventPromise.promise,
      })
    );

    try {
      const payload = {
        items,
      };

      if (sectionIds.length > 0) {
        payload.sections =
          sectionIds.join(',');
      }

      const response =
        await fetch(
          Theme.routes.cart_add_url,
          {
            method:
              'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json',
            },

            credentials:
              'same-origin',

            body:
              JSON.stringify(
                payload
              ),
          }
        );

      const result =
        await response.json();

      /* =====================================================
         ERROR
      ===================================================== */

      if (
        !response.ok ||
        result.status
      ) {
        const message =
          result.description ||
          result.message ||
          this.dataset.errorMessage ||
          'Unable to add the set to cart.';

        this.dispatchEvent(
          new CartErrorEvent({
            error:
              message,

            code:
              'INVALID',

            detail: {
              description:
                result.description,

              errors:
                result.errors,
            },
          })
        );

        try {
          const cart =
            await this.#fetchCart();

          deferredEventPromise.resolve({
            cart:
              CartLinesUpdateEvent
                .createCartFromAjaxResponse(
                  cart
                ),

            detail: {
              didError:
                true,

              items:
                cart.items,

              source:
                'companion-product-selector',

              sourceId:
                this.dataset.blockId,

              itemCount:
                0,

              productId:
                this.dataset.productId,
            },
          });
        } catch (cartError) {
          deferredEventPromise.reject(
            cartError
          );
        }

        this.#showError(
          message
        );

        return;
      }

      /* =====================================================
         SUCCESS
      ===================================================== */

      const cart =
        await this.#fetchCart();

      deferredEventPromise.resolve({
        cart:
          CartLinesUpdateEvent
            .createCartFromAjaxResponse(
              cart
            ),

        detail: {
          didError:
            false,

          items:
            cart.items,

          source:
            'companion-product-selector',

          sourceId:
            this.dataset.blockId,

          /*
           * Quantity 3 set:
           *
           * Insert × 3
           * Wall Plate × 3
           *
           * Total cart units added = 6
           */
          itemCount:
            quantity * 2,

          productId:
            this.dataset.productId,

          sections:
            result.sections,
        },
      });

      this.#updateProductFormCartQuantity(
        productForm,
        cart
      );

      this.#showSuccess(
        'Set added to cart.'
      );
    } catch (error) {
      console.error(
        '[companion-product-selector]',
        error
      );

      deferredEventPromise.reject(
        error
      );

      const message =
        error?.message ||
        this.dataset.errorMessage ||
        'Something went wrong. Please try again.';

      this.dispatchEvent(
        new CartErrorEvent({
          error:
            message,

          code:
            'SERVICE_UNAVAILABLE',
        })
      );

      this.#showError(
        message
      );
    } finally {
      this.#isSubmitting =
        false;

      addToCartContainers.forEach(
        (container) => {
          if (
            typeof container.enable === 'function'
          ) {
            container.enable();
          }
        }
      );
    }
  }

  /* =========================================================
     CART SECTION IDS
  ========================================================= */

  #getCartSectionIds() {
    const ids = [];

    document
      .querySelectorAll(
        'cart-items-component'
      )
      .forEach((component) => {
        if (
          component instanceof HTMLElement &&
          component.dataset.sectionId
        ) {
          ids.push(
            component.dataset.sectionId
          );
        }
      });

    return [
      ...new Set(
        ids
      ),
    ];
  }

  /* =========================================================
     FETCH CURRENT CART
  ========================================================= */

  async #fetchCart() {
    const cartItemsComponent =
      document.querySelector(
        'cart-items-component'
      );

    if (cartItemsComponent) {
      await customElements.whenDefined(
        'cart-items-component'
      );

      if (
        typeof cartItemsComponent.fetchCartData ===
        'function'
      ) {
        return cartItemsComponent.fetchCartData();
      }
    }

    const response =
      await fetch(
        `${Theme.routes.cart_url}.json`,
        {
          headers: {
            Accept:
              'application/json',
          },

          credentials:
            'same-origin',
        }
      );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch cart: ${response.status}`
      );
    }

    return response.json();
  }

  /* =========================================================
     UPDATE HORIZON'S CART QUANTITY DATA
  ========================================================= */

  #updateProductFormCartQuantity(
    productForm,
    cart
  ) {
    const mainVariantId =
      this.#getMainVariantId(
        productForm
      );

    if (!mainVariantId) {
      return;
    }

    const cartQuantity =
      cart.items
        .filter(
          (item) =>
            Number(
              item.variant_id
            ) ===
            Number(
              mainVariantId
            )
        )
        .reduce(
          (total, item) =>
            total +
            Number(
              item.quantity || 0
            ),

          0
        );

    const quantitySelector =
      productForm.refs?.quantitySelector;

    if (
      quantitySelector &&
      typeof quantitySelector.setCartQuantity ===
        'function'
    ) {
      quantitySelector.setCartQuantity(
        cartQuantity
      );
    }
  }

  /* =========================================================
     MAIN INSERT VARIANT CHANGES
  ========================================================= */

  #handleProductSelect = (event) => {
    if (
      event.target instanceof Element &&
      event.target.closest(
        'product-card'
      )
    ) {
      return;
    }

    event.promise
      ?.then(
        ({ detail }) => {
          if (!detail?.html) {
            return;
          }

          const blockId =
            CSS.escape(
              this.dataset.blockId || ''
            );

          const newBlock =
            detail.html.querySelector(
              `companion-product-selector[data-block-id="${blockId}"]`
            );

          if (
            !(newBlock instanceof HTMLElement)
          ) {
            return;
          }

          /*
           * Entire product changed.
           */
          if (
            newBlock.dataset.productId &&
            newBlock.dataset.productId !==
              this.dataset.productId
          ) {
            this.replaceWith(
              newBlock.cloneNode(true)
            );

            return;
          }

          /*
           * Same product:
           * Insert variant changed.
           */
          this.dataset.mainVariantId =
            newBlock.dataset.mainVariantId ||
            this.dataset.mainVariantId;

          this.dataset.mainPrice =
            newBlock.dataset.mainPrice ||
            this.dataset.mainPrice;

          this.dataset.mainAvailable =
            newBlock.dataset.mainAvailable ||
            this.dataset.mainAvailable;
        }
      )
      .catch(
        (error) => {
          if (
            error?.name !==
            'AbortError'
          ) {
            console.warn(
              '[companion-product-selector] Product update failed:',
              error
            );
          }
        }
      );
  };

  /* =========================================================
     STATUS
  ========================================================= */

  #showError(message) {
    const status =
      this.querySelector(
        '[data-status]'
      );

    if (!status) {
      return;
    }

    status.dataset.error =
      'true';

    status.textContent =
      message;
  }

  #showSuccess(message) {
    const status =
      this.querySelector(
        '[data-status]'
      );

    if (!status) {
      return;
    }

    status.removeAttribute(
      'data-error'
    );

    status.textContent =
      message;
  }

  #clearStatus() {
    const status =
      this.querySelector(
        '[data-status]'
      );

    if (!status) {
      return;
    }

    status.removeAttribute(
      'data-error'
    );

    status.textContent =
      '';
  }
}

if (
  !customElements.get(
    'companion-product-selector'
  )
) {
  customElements.define(
    'companion-product-selector',
    CompanionProductSelector
  );
}